import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faUserShield, faInfoCircle, faSearch, faFilter } from "@fortawesome/free-solid-svg-icons";

const UserRolePage_v2 = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [showOnlyUserRoles, setShowOnlyUserRoles] = useState(false); // Toggle filter state

  useEffect(() => {
    fetchRoles();
    fetchUserRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/admin/user_roles`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
        },
      });
      setRoles(response.data);
    } catch (error) {
      console.error("Error fetching roles:", error);
    } finally {
      setIsLoading(false); // Stop loading after fetch
    }
  };

  const fetchUserRoles = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/admin/users/${username}/roles`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
        },
      });
      setUserRoles(response.data);
    } catch (error) {
      console.error("Error fetching user roles:", error);
    }
  };

  const handleRoleChange = async (roleId, isChecked) => {
    try {
      const method = isChecked ? "post" : "delete";
      await axios[method](
        `${BASE_URL}/admin/users/${username}/roles/${roleId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        }
      );
      Swal.fire("Success!", "Role updated successfully.", "success");
      fetchUserRoles();
    } catch (error) {
      Swal.fire("Error!", "Failed to update role.", "error");
    }
  };

  const filteredRoles = roles.filter((role) => {
    const matchesSearch = role.role_name.toLowerCase().includes(searchTerm.toLowerCase());
    if (showOnlyUserRoles) {
      return matchesSearch && userRoles.some((ur) => ur.role_id === role.role_id);
    }
    return matchesSearch;
  });

  return (
    <div className="bg-gray-50 min-h-screen p-2">
      <div className="max-w-1xl mx-auto bg-white rounded-lg shadow-sm p-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-blue-500 hover:text-blue-700 mb-3 text-sm"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back
        </button>
        <h1 className="text-xl font-bold mb-4 flex items-center">
          <FontAwesomeIcon icon={faUserShield} className="mr-2" />
          User Roles for {username}
        </h1>

        {/* Search Bar and Toggle Filter */}
        <div className="mb-4 flex items-center">
          <div className="relative flex-grow">
            <input
              type="text"
              placeholder="Search roles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-1.5 pl-8 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
            />
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-2.5 top-2.5 text-gray-400 text-sm"
            />
          </div>
          <button
            onClick={() => setShowOnlyUserRoles(!showOnlyUserRoles)}
            className={`ml-2 p-2 rounded-md text-sm flex items-center ${
              showOnlyUserRoles ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"
            }`}
          >
            <FontAwesomeIcon icon={faFilter} className="mr-2" />
            {showOnlyUserRoles ? "Show All Roles" : "Show Only My Roles"}
          </button>
        </div>

        {/* Scrollable Roles Container */}
        <div className="overflow-y-auto max-h-[50vh] pr-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {isLoading ? (
              // Loading Skeleton
              Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={index}
                  className="bg-gray-100 p-3 rounded-md animate-pulse"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="h-4 w-4 bg-gray-300 rounded"></div>
                      <div className="ml-2 h-4 bg-gray-300 rounded w-1/2"></div>
                    </div>
                    <div className="h-4 w-4 bg-gray-300 rounded"></div>
                  </div>
                  <div className="mt-2 h-3 bg-gray-300 rounded w-3/4"></div>
                </div>
              ))
            ) : (
              // Actual Roles
              filteredRoles.map((role) => (
                <div
                  key={role.role_id}
                  className="bg-gray-100 p-3 rounded-md hover:bg-gray-200 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={userRoles.some((ur) => ur.role_id === role.role_id)}
                        onChange={(e) => handleRoleChange(role.role_id, e.target.checked)}
                        className="form-checkbox h-4 w-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-base font-medium">{role.role_name}</span>
                    </div>
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className="text-gray-400 hover:text-gray-600 cursor-pointer text-sm"
                    />
                  </div>
                  {role.description && (
                    <p className="text-xs text-gray-600 mt-1">{role.description}</p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserRolePage_v2;
